import { useState } from 'react';

import { useMeetingStatusActions } from './useMeetingStatusActions';

import type { ThematicMeetingListItem } from '@/types/thematic-meetings';

import { useDeleteThematicMeeting } from '@/api/thematic-meetings';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';

/**
 * What a meeting card offers from the list: complete, reopen and delete. A deletion is asked for
 * first and stays asked while it fails.
 */
export const useMeetingListActions = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const statusActions = useMeetingStatusActions();
    const deleteMeeting = useDeleteThematicMeeting();

    const [meetingToDelete, setMeetingToDelete] = useState<ThematicMeetingListItem | null>(null);

    const confirmDelete = async () => {
        if (!meetingToDelete) {
            return;
        }

        try {
            await deleteMeeting.mutateAsync(meetingToDelete.id);
            showSuccessToast({ message: t('thematicMeetings.list.deleteMeetingSuccess') });
            setMeetingToDelete(null);
        } catch (error) {
            handleError({ error });
        }
    };

    return {
        markCompleted: (meeting: ThematicMeetingListItem) =>
            statusActions.markCompleted(meeting.id),
        reopen: (meeting: ThematicMeetingListItem) => statusActions.reopen(meeting.id),
        meetingToDelete,
        requestDelete: setMeetingToDelete,
        cancelDelete: () => setMeetingToDelete(null),
        confirmDelete,
        isDeleting: deleteMeeting.isPending,
    };
};
